import { useEffect, useState } from "react";
import { getTokenStatus } from "@/api/companion";
import type { IntegrationConnection } from "@/api/integrations";
import type { PocketRowProps } from "@/components/ui";
import { useAuth } from "@/hooks/use-auth";
import { translate, useT } from "@/i18n";
import { settingsConnectionsMessages } from "@/i18n/messages/settings-connections";
import type en from "@/i18n/messages/settings-connections/en";
import { COMPOSIO_UI_ENABLED } from "@/lib/composio";
import { CLI_REGISTRY } from "@/lib/cli-install-commands";
import { detectOS } from "@/lib/detect-os";
import {
  useCompanionStore,
  type AvailableRuntime,
} from "@/stores/companion-store";
import { useIntegrationStore } from "@/stores/integration-store";

export const CONNECTION_TOOLS = [
  { id: "claude_cli", name: "Claude Code", registry: "claude" },
  { id: "codex_cli", name: "Codex", registry: "codex" },
  { id: "opencode_cli", name: "OpenCode", registry: "opencode" },
] as const;
/** AI tools explicitly signed out (Preferences → AI tools). */
export function countRuntimesNeedingAttention(
  runtimes: readonly AvailableRuntime[],
) {
  return new Set(
    runtimes
      .filter((runtime) => runtime.logged_in === false)
      .map((runtime) => runtime.type),
  ).size;
}
/** App accounts whose access expired or failed (Connections). */
export function countAppsNeedingAttention(
  connections: ReadonlyMap<string, IntegrationConnection>,
  appsEnabled = COMPOSIO_UI_ENABLED,
) {
  if (!appsEnabled) return 0;
  return [...connections.values()].filter(
    (connection) =>
      connection.toolkit_slug !== "composio" &&
      ["expired", "failed"].includes(connection.status),
  ).length;
}
/** The row status for an attention count; none when nothing needs attention. */
export function attentionStatus(count: number): PocketRowProps["status"] {
  return count
    ? {
        tone: "question",
        text: translate(settingsConnectionsMessages, "needAttention", {
          count,
        }),
      }
    : undefined;
}
export function useRuntimeAttentionCount() {
  const runtimes = useCompanionStore((state) => state.availableRuntimes);
  return countRuntimesNeedingAttention(runtimes);
}
export function useAppAttentionCount() {
  const connections = useIntegrationStore((state) => state.connections);
  return countAppsNeedingAttention(connections);
}
type RuntimeState =
  | "notInstalled"
  | "notSignedIn"
  | "updateAvailable"
  | "ready"
  | "signInUnknown";
const RUNTIME_KEYS = {
  notInstalled: {
    status: "runtimeNotInstalled",
    title: "toolTitleNotInstalled",
  },
  notSignedIn: { status: "runtimeNotSignedIn", title: "toolTitleNotSignedIn" },
  updateAvailable: {
    status: "runtimeUpdateAvailable",
    title: "toolTitleUpdateAvailable",
  },
  ready: { status: "runtimeReady", title: "toolTitleReady" },
  signInUnknown: {
    status: "runtimeSignInUnknown",
    title: "toolTitleSignInUnknown",
  },
} as const satisfies Record<
  RuntimeState,
  { status: keyof typeof en; title: keyof typeof en }
>;
function runtimeState(runtime: AvailableRuntime | undefined): RuntimeState {
  if (!runtime) return "notInstalled";
  if (runtime.logged_in === false) return "notSignedIn";
  if (runtime.update_available) return "updateAvailable";
  return runtime.logged_in === true ? "ready" : "signInUnknown";
}
/** The AI tool's status words: "Ready", "Not signed in". */
export function runtimeStatus(runtime: AvailableRuntime | undefined) {
  return translate(
    settingsConnectionsMessages,
    RUNTIME_KEYS[runtimeState(runtime)].status,
  );
}
/** The AI tool's page title: "Codex: not signed in." */
export function runtimeTitle(
  name: string,
  runtime: AvailableRuntime | undefined,
) {
  return translate(
    settingsConnectionsMessages,
    RUNTIME_KEYS[runtimeState(runtime)].title,
    { name },
  );
}
export function runtimeCommand(
  tool: string,
  runtime: AvailableRuntime | undefined,
) {
  const info = CONNECTION_TOOLS.find((item) => item.id === tool);
  if (!info) return "";
  const fallback = CLI_REGISTRY[info.registry];
  if (!runtime) return fallback.commands[detectOS() ?? "macos"];
  if (runtime.logged_in === false)
    return runtime.login_command || fallback.loginCommand;
  if (runtime.update_available && !runtime.managed)
    return runtime.update_command || fallback.commands[detectOS() ?? "macos"];
  return "";
}
/** Runtime refresh responses are scoped to the account and mounted page. */
export function useConnectionRuntimeRefresh(poll: boolean) {
  const { user } = useAuth();
  const t = useT(settingsConnectionsMessages);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    const refresh = () => {
      useCompanionStore.getState().sendRuntimeRefresh?.(true);
      void getTokenStatus()
        .then((info) => {
          if (!active) return;
          setFailed(false);
          if (info.available_runtimes)
            useCompanionStore
              .getState()
              .setAvailableRuntimes(
                info.available_runtimes,
                info.default_runtime ?? "claude_cli",
              );
        })
        .catch(() => {
          if (active) setFailed(true);
        });
    };
    refresh();
    window.addEventListener("focus", refresh);
    window.addEventListener("denker:check-settings-tools", refresh);
    const interval = poll ? window.setInterval(refresh, 5000) : undefined;
    return () => {
      active = false;
      window.removeEventListener("focus", refresh);
      window.removeEventListener("denker:check-settings-tools", refresh);
      if (interval) window.clearInterval(interval);
    };
  }, [user?.id, poll]);
  return {
    error: failed ? t("toolCheckFailed") : null,
    refresh: () =>
      window.dispatchEvent(new Event("denker:check-settings-tools")),
  };
}
